import classNames from 'classnames';
import React, { useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { REMOTE } from 'constants/appConstants';
import { WORK_STATUS } from 'constants/dashboard';
import { WFO_BUTTON, WFH_BUTTON } from 'constants/floatingButtons';

import StatTile from './StatTile';
import ClockInTime from './ClockInTime';
import CalendarCross from 'components/common/icons';
import { ClockInHistoryModal } from './clockInHistory/ClockInHistoryModal';
import { ClockInHistoryButton } from './clockInHistory/ClockInHistoryButton';

import type { WorkStatusSummary } from 'interface/leave';
import type { CalendarDay, UnfreezeRequest } from 'interface/attendance';

interface WorkStatusCardProps {
  isLoading: boolean;
  shouldShowWorkshift: boolean;
  medianTime: string | null;
  clockinHistory: CalendarDay[];
  isClockinHistoryLoading: boolean;
  unfreezeRequests: UnfreezeRequest[];
  summary: WorkStatusSummary;
}

const WfoIcon = WFO_BUTTON.icon;
const WfhIcon = WFH_BUTTON.icon;

const WORK_STATUS_TILE_META: Record<
  keyof WorkStatusSummary,
  { label: string; variant: 'blue' | 'orange' | 'purple'; icon: React.ReactNode }
> = {
  wfo: { label: WORK_STATUS.WFO, variant: 'orange', icon: <WfoIcon size={18} /> },
  wfh: { label: REMOTE, variant: 'blue', icon: <WfhIcon size={18} /> },
  leave: {
    label: WORK_STATUS.LEAVE,
    variant: 'purple',
    icon: <CalendarCross size={18} />,
  },
};

export default function WorkStatusCard({
  isLoading,
  shouldShowWorkshift,
  medianTime,
  clockinHistory,
  isClockinHistoryLoading,
  unfreezeRequests,
  summary,
}: WorkStatusCardProps) {
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  return (
    <div className="full-scope-card dashboard__card work-status d-flex flex-direction-column">
      <div className="dashboard__title dashboard__title--allocation">
        <div className="dashboard__title--allocation-text">
          <div>Work Status</div>
        </div>

        {!isLoading && shouldShowWorkshift && <ClockInTime medianClockInTime={medianTime} />}
      </div>

      {isLoading ? (
        <SkeletonTheme baseColor="#fdfdfd" highlightColor="#f0f3f5">
          <Skeleton className="median-office-time-card-loader wp-100" height={112} />
        </SkeletonTheme>
      ) : (
        <div
          className={classNames('actions median-office-time px-16 pbpx-16 d-flex flex-direction-column gap-px-12', {
            'flex-1 justify-content-center': !shouldShowWorkshift,
          })}
        >
          {shouldShowWorkshift && (
            <ClockInHistoryModal
              TriggerBtn={() => <ClockInHistoryButton setIsHistoryModalOpen={setIsHistoryModalOpen} />}
              isOpen={isHistoryModalOpen}
              onClose={() => setIsHistoryModalOpen(false)}
              clockinHistoryData={clockinHistory}
              isClockinHistoryLoading={isClockinHistoryLoading}
              medianClockInTime={medianTime}
              unfreezeRequests={unfreezeRequests}
            />
          )}

          <div className="work-status__tiles d-flex">
            {(Object.entries(summary) as Array<[keyof WorkStatusSummary, number]>).map(([status, count]) => {
              const { label, variant, icon } = WORK_STATUS_TILE_META[status];

              return <StatTile key={status} label={label} count={count} variant={variant} icon={icon} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
